import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ChevronRight from "@/assets/svg/chevronRight.svg";
import Timeline from "@/components/ThreeJSVideoAnimation/TimelineNavigation/Timeline";
import { VideoFrame } from "../types";
import { motion } from "motion/react";
import ViewImageIcon from "@/assets/svg/viewImage.svg";
import TimelineLightbox from "@/components/ThreeJSVideoAnimation/TimelineNavigation/TimelineLightbox";
import { usePreventScroll } from "@/hooks/usePreventScroll";

const NavigationContainer = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 10;
  inset: 0;
`;

const NavigationWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const TopLayer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  padding: 20px;
  width: 100%;
  gap: 4px;
`;

const BottomLayer = styled.div`
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  padding: 4px 0;
  width: 100%;
  gap: 16px;
  display: flex;
`;

const Title = styled(motion.h2)`
  color: #f7f8f8;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
`;

const Description = styled(motion.p)`
  color: #8a8f98;
  font-family: Inter;
  font-size: 10px;
  font-weight: 400;
  line-height: 11px;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
`;

const NavigationButton = styled.div<{ $disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 0.5px solid rgba(62, 62, 68, 0.6);
  background: rgba(40, 40, 44, 0.797);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  color: #f7f8f8;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  transition: opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: #f7f8f8;
  }

  ${(props) =>
    props.$disabled &&
    `
    pointer-events: none;
  `}
`;

interface TimelineNavigationProps {
  videos: VideoFrame[];
  currentVideoIndex: number;
  previousVideoIndex: number;
  targetVideoIndex: number;
  transitionDurationMs: number;
  framesPath: string;
  onVideoSelect: (index: number) => void;
  isTransitioning: boolean;
  disabled?: boolean;
  preloadStatus: any; // Import the type from useImagePreloader if needed
  navigateNext: () => void;
  navigatePrev: () => void;
}

export default function TimelineNavigation({
  videos,
  currentVideoIndex,
  previousVideoIndex,
  targetVideoIndex,
  transitionDurationMs,
  framesPath,
  onVideoSelect,
  isTransitioning,
  disabled = false,
  preloadStatus,
  navigateNext,
  navigatePrev,
}: TimelineNavigationProps) {
  const [displayLightbox, setDisplayLightbox] = useState(false);

  if (!videos || videos.length === 0) {
    return null;
  }

  const progressPercentage = videos.length > 1 ? (currentVideoIndex / (videos.length - 1)) * 100 : 0;

  const currentVideo = videos[currentVideoIndex];

  // Check if navigation buttons should be disabled
  const getNextVideoIndex = () => (currentVideoIndex + 1) % videos.length;
  const getPrevVideoIndex = () => (currentVideoIndex - 1 + videos.length) % videos.length;

  const nextVideoIndex = getNextVideoIndex();
  const prevVideoIndex = getPrevVideoIndex();

  // Check if target videos are navigable
  const isNextNavigable = preloadStatus?.videoStates?.[nextVideoIndex]?.loadedFrames > 0;
  const isPrevNavigable = preloadStatus?.videoStates?.[prevVideoIndex]?.loadedFrames > 0;

  // Disable next button if next video isn't loaded
  const isNextDisabled = disabled || isTransitioning || !isNextNavigable;
  // Disable prev button if prev video isn't loaded
  const isPrevDisabled = disabled || isTransitioning || !isPrevNavigable;

  const openLightbox = (video: VideoFrame) => {
    setDisplayLightbox(true);
  };

  return (
    <>
      {displayLightbox && (
        <TimelineLightbox currentVideo={currentVideo} framesPath={framesPath} setDisplayLightbox={setDisplayLightbox} />
      )}
      <NavigationContainer>
        <NavigationWrapper>
          <TopLayer>
            <Title animate={{ opacity: isTransitioning ? 0 : 1 }} transition={{ duration: 0.5 }}>
              {currentVideo?.title}
            </Title>
            <Description animate={{ opacity: isTransitioning ? 0 : 1 }} transition={{ duration: 0.5 }}>
              {currentVideo?.description}
            </Description>
          </TopLayer>
          <BottomLayer>
            <NavigationButtons>
              <NavigationButton
                style={{ transform: "rotate(180deg)" }}
                onClick={isPrevDisabled ? undefined : navigatePrev}
                $disabled={isPrevDisabled}>
                <ChevronRight />
              </NavigationButton>
              <NavigationButton onClick={isNextDisabled ? undefined : navigateNext} $disabled={isNextDisabled}>
                <ChevronRight />
              </NavigationButton>
              <NavigationButton onClick={() => openLightbox(currentVideo)}>
                <ViewImageIcon fill='#f7f8f8e5' />
              </NavigationButton>
            </NavigationButtons>
            <Timeline
              videos={videos}
              currentVideoIndex={currentVideoIndex}
              previousVideoIndex={previousVideoIndex}
              targetVideoIndex={targetVideoIndex}
              transitionDurationMs={transitionDurationMs}
              framesPath={framesPath}
              onVideoSelect={onVideoSelect}
              isTransitioning={isTransitioning}
              preloadStatus={preloadStatus}
            />
          </BottomLayer>
        </NavigationWrapper>
      </NavigationContainer>
    </>
  );
}
